import { z } from 'zod';

/**
 * Password rules for team accounts (console). ASVS L1: at least 12 characters and no
 * common or guessable passwords. The Pro-only leaked-password check is off on the
 * free plan, so this list-and-pattern check is the guard; only the account owner
 * could bypass it, by calling the API directly.
 */
export const PASSWORD_MIN_CHARS = 12;
/** Supabase hashes passwords with bcrypt, which reads at most 72 bytes. */
export const PASSWORD_MAX_BYTES = 72;

export type PasswordProblem = 'too_short' | 'too_long' | 'common';

/**
 * Parts that make a password easy to guess: the product, keyboard walks, number runs
 * and the words that top every leaked-password list (English and Colombian Spanish).
 * Any password that contains one of them is rejected.
 */
const GUESSABLE_PARTS = [
  // the product and the obvious
  'antoja',
  'consola',
  'password',
  'passw0rd',
  'contrasena',
  'contraseña',
  'clave',
  'admin',
  'welcome',
  'bienvenido',
  'iloveyou',
  'teamo',
  'tequiero',
  'letmein',
  'trustno1',
  // keyboard walks and runs
  'qwerty',
  'asdfgh',
  'zxcvbn',
  'qazwsx',
  '1qaz2wsx',
  'zaq1',
  '1q2w3e',
  'q1w2e3',
  'qwer1234',
  '123456',
  '654321',
  'abcdef',
  '111111',
  '000000',
  '121212',
  '112233',
  '123123',
  '696969',
  // the most common words in leaked lists
  'sunshine',
  'football',
  'baseball',
  'princess',
  'princesa',
  'dragon',
  'monkey',
  'master',
  'shadow',
  'superman',
  'batman',
  'starwars',
  'pokemon',
  'whatever',
  'freedom',
  'computer',
  'internet',
  'samsung',
  'iphone',
  'google',
  'facebook',
  'instagram',
  'michael',
  'jennifer',
  'charlie',
  'jordan',
  'liverpool',
  'barcelona',
  'realmadrid',
  'chelsea',
  'arsenal',
  'mariposa',
  'estrella',
  'corazon',
  'amorcito',
  'familia',
  'futbol',
  'nacional',
  'millonarios',
  'america',
  'colombia',
  'medellin',
  'bogota',
  'barranquilla',
  'cartagena',
  'bucaramanga',
  'pereira',
  'manizales',
  'valledupar',
];

/** Longest run of characters that go up or down one by one ("abcd", "4321"). */
function longestSequence(text: string): number {
  let longest = 1;
  let up = 1;
  let down = 1;
  for (let i = 1; i < text.length; i += 1) {
    const step = text.charCodeAt(i) - text.charCodeAt(i - 1);
    up = step === 1 ? up + 1 : 1;
    down = step === -1 ? down + 1 : 1;
    longest = Math.max(longest, up, down);
  }
  return longest;
}

export function passwordProblem(password: string, email?: string): PasswordProblem | null {
  if ([...password].length < PASSWORD_MIN_CHARS) return 'too_short';
  if (new TextEncoder().encode(password).length > PASSWORD_MAX_BYTES) return 'too_long';
  const plain = password.toLowerCase();
  const localPart = email?.split('@')[0]?.toLowerCase() ?? '';
  if (
    new Set(plain).size < 5 ||
    longestSequence(plain) >= 6 ||
    GUESSABLE_PARTS.some((part) => plain.includes(part)) ||
    (localPart.length >= 4 && plain.includes(localPart))
  ) {
    return 'common';
  }
  return null;
}

/** The same rule as a schema, for forms and boundaries. */
export const TeamPasswordSchema = z.string().superRefine((value, context) => {
  const problem = passwordProblem(value);
  if (problem) context.addIssue({ code: 'custom', message: problem });
});
